// ./src/components/features/Connectors/ConfigEditors/AwsSnsConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Select,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AwsSnsConnectorConfig } from "@/types";

export interface AwsSnsConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: AwsSnsConnectorConfig = {
  accessKeyId: "",
  secretAccessKey: "",
  region: "",
  topicArn: "",
  messageSubject: "",
  messageGroupId: "",
  messageDeduplicationId: "",
};

// Common AWS regions
const AWS_REGIONS = [
  { value: "us-east-1", label: "US East (N. Virginia)" },
  { value: "us-east-2", label: "US East (Ohio)" },
  { value: "us-west-1", label: "US West (N. California)" },
  { value: "us-west-2", label: "US West (Oregon)" },
  { value: "ca-central-1", label: "Canada (Central)" },
  { value: "eu-west-1", label: "Europe (Ireland)" },
  { value: "eu-west-2", label: "Europe (London)" },
  { value: "eu-west-3", label: "Europe (Paris)" },
  { value: "eu-central-1", label: "Europe (Frankfurt)" },
  { value: "eu-north-1", label: "Europe (Stockholm)" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" },
  { value: "ap-northeast-2", label: "Asia Pacific (Seoul)" },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
  { value: "ap-southeast-2", label: "Asia Pacific (Sydney)" },
  { value: "ap-south-1", label: "Asia Pacific (Mumbai)" },
  { value: "sa-east-1", label: "South America (São Paulo)" },
];

/**
 * Parse and validate AWS SNS connector configuration
 */
const parseAwsSnsConfig = (jsonString: string): AwsSnsConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

/**
 * Check if topic ARN indicates a FIFO topic
 */
const isFifoTopic = (topicArn: string): boolean => {
  return topicArn.endsWith(".fifo");
};

/**
 * Validate ARN format
 */
const isValidArn = (arn: string): boolean => {
  return arn.startsWith("arn:aws:sns:");
};

export const AwsSnsConnectorConfigEditor: React.FC<
  AwsSnsConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AwsSnsConnectorConfig>(() =>
    parseAwsSnsConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAwsSnsConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AwsSnsConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleAccessKeyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, accessKeyId: e.target.value });
  };

  const handleSecretAccessKeyChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, secretAccessKey: e.target.value });
  };

  const handleRegionChange = (value: string | null) => {
    updateConfig({ ...config, region: value || "" });
  };

  const handleTopicArnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, topicArn: e.target.value });
  };

  const handleMessageSubjectChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, messageSubject: e.target.value });
  };

  const handleMessageGroupIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, messageGroupId: e.target.value });
  };

  const handleMessageDeduplicationIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, messageDeduplicationId: e.target.value });
  };

  const isFifo = isFifoTopic(config.topicArn || "");

  // Validate topic ARN format
  const topicArnError = (() => {
    if (!config.topicArn) return undefined;
    if (!isValidArn(config.topicArn)) {
      return "Must be a valid ARN (arn:aws:sns:...)";
    }
    return undefined;
  })();

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Create access keys in{" "}
          <Anchor
            href="https://console.aws.amazon.com/iam/"
            target="_blank"
            rel="noopener noreferrer"
          >
            AWS IAM Console
          </Anchor>
          {". Find topic ARN in "}
          <Anchor
            href="https://console.aws.amazon.com/sns/"
            target="_blank"
            rel="noopener noreferrer"
          >
            SNS Console
          </Anchor>
          {" → Topics → Topic details"}
        </Text>
      </Alert>

      {/* Access Key ID */}
      <TextInput
        label="Access Key ID"
        placeholder="AKIAIOSFODNN7EXAMPLE"
        description="AWS Access Key ID"
        value={config.accessKeyId}
        onChange={handleAccessKeyIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Secret Access Key */}
      <TextInput
        label="Secret Access Key"
        placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
        description="AWS Secret Access Key"
        value={config.secretAccessKey}
        onChange={handleSecretAccessKeyChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Region */}
      <Select
        label="Region"
        placeholder="Select AWS region"
        description="AWS region where the topic is located"
        data={AWS_REGIONS}
        value={config.region}
        onChange={handleRegionChange}
        disabled={disabled}
        required
        size="md"
        searchable
      />

      {/* Topic ARN */}
      <TextInput
        label="Topic ARN"
        placeholder="arn:aws:sns:us-east-1:123456789012:my-topic"
        description={
          isFifo ? "FIFO topic detected" : "Standard or FIFO topic ARN"
        }
        value={config.topicArn}
        onChange={handleTopicArnChange}
        disabled={disabled}
        required
        size="md"
        error={topicArnError}
      />

      {/* Message Subject (useful for email subscriptions) */}
      <TextInput
        label="Message Subject"
        placeholder="New submission from {{formName}}"
        description="Subject line for email subscriptions (optional)"
        value={config.messageSubject || ""}
        onChange={handleMessageSubjectChange}
        disabled={disabled}
        size="md"
      />

      {/* FIFO-specific fields */}
      {isFifo && (
        <>
          <TextInput
            label="Message Group ID"
            placeholder="{{formId}}"
            description="Required for FIFO topics. Messages in the same group are delivered in order."
            value={config.messageGroupId || ""}
            onChange={handleMessageGroupIdChange}
            disabled={disabled}
            size="md"
          />

          <TextInput
            label="Message Deduplication ID"
            placeholder="{{requestId}}"
            description="Prevents duplicate messages within 5-minute window"
            value={config.messageDeduplicationId || ""}
            onChange={handleMessageDeduplicationIdChange}
            disabled={disabled}
            size="md"
          />
        </>
      )}

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{requestId}}"}</Code> — Submission ID (e.g.,
          20250530143022-ABCD)
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{formName}}"}</Code> — Form name (sanitized)
          <br />
          <Code>{"{{timestamp}}"}</Code> — Unix timestamp
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
          <br />
          <Code>{"{{randUuid}}"}</Code> — Random UUID
        </Text>
      </Alert>
    </Stack>
  );
};
